<script>
  import Modal from "@components/shared/modal.svelte";
  import { get, post } from "@lib/requests";
  import { cn, formatMoney, showAlert, getSafeParam } from "@lib/utils";
  import { onMount } from "svelte";

  let ids = $state(getSafeParam("send_ids[]", { transform: (value) => parseInt(value, 10) }));
  let open = $state(false);
  let pdfOpen = $state(false);

  let loading = $state(false);
  let loaded = $state(false);
  let error = $state(null);
  let invoices = $state([])
  let selectedInvoices = $state([]);
  let pdfData = $state(null);

  let unsendableInvoices = $derived(invoices.filter(invoice => !invoice.sendable));
  let sendables = $derived(invoices.filter(invoice => invoice.sendable && selectedInvoices.includes(invoice.id)));
  let unsendables = $derived(invoices.filter(invoice => !invoice.sendable && selectedInvoices.includes(invoice.id)));

  let allSelected = $derived(invoices.length > 0 && selectedInvoices.length === invoices.length);
  let partSelected = $derived(invoices.length > 0 && selectedInvoices.length > 0 && selectedInvoices.length < invoices.length);

  async function loadInvoices() {
    try {
      loading = true;
      let path = '/api/v1/invoices/sendable';
      if (ids?.length > 0) path = `${path}?${ids.map(id => `ids[]=${id}`).join("&")}`;
      const response = await get(path)
      const data = await response.json;
      invoices = data;
    } catch (e) {
      error = e;
    } finally {
      loading = false;
      loaded = true;
    }
  }

  async function refetch() {
    await loadInvoices();
    selectedInvoices = []
  }

  function openModal() {
    open = true;
    if (ids.length > 0) selectedInvoices = ids;
    if (!loaded) loadInvoices();
  }

  function closeModal() {
    open = false;
    invoices = [];
    loaded = false;
    selectedInvoices = [];
    pdfData = null;
    pdfOpen = false;
  }

  function toggleAll(event) {
    if (allSelected) {
      selectedInvoices = []
    } else {
      selectedInvoices = invoices.map(invoice => invoice.id);
    }
  }

  function toggleInvoice(invoiceId) {
    if (selectedInvoices.includes(invoiceId)) {
      selectedInvoices = selectedInvoices.filter(id => id !== invoiceId);
    } else {
      selectedInvoices = [...selectedInvoices, invoiceId];
    }
  }

  async function printInvoices() {
    const resources = unsendables.map(invoice => `invoice_${invoice.id}`).join(",");

    if (!window.confirm(`Imprimer les ${unsendables.length} facture(s) sélectionnée(s) et les marquer comme envoyées par courrier ?`)) return

    try {
      const response = await post(`/envois/groupes.json`, { body: { resources, type: 'post', print: true } }, { responseKind: 'json' })
      const data = await response.json;

      if (response.ok && data.data) {
        pdfData = data.data;
        pdfOpen = true;
        await refetch();
      } else {
        showAlert("L'impression des factures sélectionnées a échoué", { type: 'error' });
      }
    } catch (e) {
      showAlert(e.message, { type: 'error' });
    }
  }

  async function sendInvoices() {
    const resources = sendables.map(invoice => `invoice_${invoice.id}`).join(",");

    if (!window.confirm(`Envoyer les ${sendables.length} facture(s) sélectionnée(s) par email ?`)) return

    try {
      await post(`/envois/groupes/auto_create`, { body: { resources, type: 'email' }, responseKind: 'turbo_stream' })
      await refetch()
    } catch (e) {
      showAlert(e.message, { type: 'error' });
    }

  }

  async function sendAndPrintInvoices() {

    let message = "";

    if (sendables.length && unsendables.length) {
      message = `Envoyer ${sendables.length} facture(s) par email et imprimer ${unsendables.length} facture(s) (et les marquer envoyées par courrier) ?`;
    } else if (sendables.length) {
      message = `Envoyer ${sendables.length} facture(s) par email ?`;
    } else if (unsendables.length) {
      message = `Imprimer ${unsendables.length} facture(s) (et les marquer envoyées par courrier) ?`;
    }

    if (!window.confirm(message)) return

    const resources = selectedInvoices.map(id => `invoice_${id}`).join(",");

    try {
      await post(`/envois/groupes/auto_create`, { body: { resources, type: 'email' }, responseKind: 'turbo_stream' })
      await refetch()
    } catch (e) {
      showAlert(e.message, { type: 'error' });
    }
  }

  onMount(() => {
    if (ids.length > 0) {
      selectedInvoices = ids;
      open = true;
    }
    loadInvoices();
  });

</script>

<button class="button" onclick={openModal}>
  A envoyer
  {#if invoices.length > 0}
    <sup class="text-xxs opacity-50 -ml-1 -mr-1">{invoices.length}</sup>
  {/if}
  </button>

<Modal bind:open title="Factures à envoyer" width="65rem" onClose={closeModal}>

  {#if loading}
    <div class="app-message">
      <i class="fal fa-spinner fa-spin"></i>
      <p>Chargement...</p>
    </div>
  {:else if error}
    <p>Erreur: {error.message}</p>
  {:else}
    {#if invoices.length === 0}
      <div class="app-message">
        <i class="fal fa-info-circle"></i>
        <p>Aucune facture à envoyer</p>
      </div>
    {:else}

    <!-- Header -->
      <div class="flex justify-between items-center mb-6">
        <p class="help-text">
          <span>Il y a {invoices.length} facture(s) dûes qui sont prêtes à être envoyées</span>
          {#if unsendableInvoices.length > 0}
            <span class="italic text-xs">(dont {unsendableInvoices.length} à imprimer)</span>
          {/if}
        </p>
        <div class="flex items-center gap-3">
          <span class="text-xs text-gray-500">{selectedInvoices.length} / {invoices.length} selectionnée(s)</span>
          <input type="checkbox" class="!w-[14px] !h-[14px]" checked={allSelected} indeterminate={partSelected} onchange={toggleAll} />
        </div>
      </div>

      <!-- Table -->
      <div class="overflow-y-auto max-h-[400px]">
        <table class="table w-full shadow-md">
          <thead>
            <tr>
              <th class="w-[5%]"></th>
              <th class="w-[20%]">Référence</th>
              <th class="w-[35%]">Client</th>
              <th class="w-[25%] right">Montant (€)</th>
              <th class="w-[15%] center">Statut</th>
            </tr>
          </thead>
          <tbody>
            {#each invoices as invoice (invoice.id)}
              <tr class:opacity-50={!selectedInvoices.includes(invoice.id)}>
                <td><input type="checkbox" checked={selectedInvoices.includes(invoice.id)} onclick={() => toggleInvoice(invoice.id)} /></td>
                <td><a href={`/comptabilite/factures/${invoice.id}`} class="hover:underline">{invoice.reference}</a></td>
                <td>
                  {invoice.client.name}
                  {#if !invoice.sendable}
                    <i class="fal fa-print text-red-500 text-xs" title="A imprimer"></i>
                  {/if}
                </td>
                <td class="right">{formatMoney(invoice.balance)}</td>
                <td class="center">
                  <div class={cn("inline-block px-2 py-1 rounded-sm text-xs border boxed", invoice.status.class)}>{invoice.status.text}</div>
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    {/if}
  {/if}

  {#snippet footerButtons()}
    <button class="button" disabled={loading || unsendables.length === 0} onclick={printInvoices}>
      Imprimer
      {#if unsendables.length > 0}<sup class="text-xxs opacity-50 -ml-1 -mr-1">{unsendables.length}</sup>{/if}
      <i class="fal fa-print"></i>
    </button>
    <button class="button" disabled={loading || sendables.length === 0} onclick={sendInvoices}>
      Envoyer
      {#if sendables.length > 0}<sup class="text-xxs opacity-50 -ml-1 -mr-1">{sendables.length}</sup>{/if}
      <i class="fal fa-envelope"></i>
    </button>
    <button class="button outline-submit" disabled={loading || !selectedInvoices.length} onclick={sendAndPrintInvoices}>
      Envoyer et imprimer
      {#if selectedInvoices.length > 0}<sup class="text-xxs opacity-50 -ml-1 -mr-1">{selectedInvoices.length}</sup>{/if}
      <i class="fal fa-paper-plane"></i>
    </button>
  {/snippet}

</Modal>

{#if pdfData}
  <Modal bind:open={pdfOpen} title="Aperçu PDF" width="70rem" height="80vh" onClose={() => pdfOpen = false}>
    <object title="Aperçu PDF" type="application/pdf" data={`data:application/pdf;base64,${pdfData}`} width="100%" height="700px"></object>
  </Modal>
{/if}
